import {ListIterateeCustom, filter, first, map, snakeCase, upperFirst} from 'lodash';
import {useEffect} from 'react';
import hash from 'shorthash2';
import {Optional} from 'utility-types';
import {AdvancedQueryColumnFilter, AdvancedQueryColumnSort} from '../api/generated/index.defs';
import {KeyOf} from '../types';
import {logger} from './logger';

export {parseJson, parseEncodedJson, encodeJson} from './json';

export function singleOrDefault<T>(collection: T[], predicate?: ListIterateeCustom<T, boolean>) {
  const filtered = filter(collection, predicate);
  if (filtered.length > 1) throw new Error('Sequence contained more than one element');

  return first(filtered);
}

export const titleCase = (input) => snakeCase(input).split('_').map(upperFirst).join(' ');

export const showIfPopulated = (template: TemplateStringsArray, ...values: any[]): string =>
  values.every((x) => !!x)
    ? template
        .map((section, index) => (!!section ? `${section}${values[index] ?? ''}` : ''))
        .join('')
    : '';

export interface AdvancedPagedSearch<T> {
  searchFieldNames: KeyOf<T>[];
  searchSearchText: string;
  searchExactMatch?: boolean;
}

export type AdvancedPagedRequest<T> = {
  page?: number;
  pageSize?: number;
  filtered?: AdvancedQueryColumnFilter[];
  sorted?: AdvancedQueryColumnSort[];
} & Optional<AdvancedPagedSearch<T>>;

export interface AdvancedPagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  pageCount: number;
  totalItemCount: number;
}

export function normalizedAdvancedPagedResult<T>(
  value: undefined | AdvancedPagedResult<T>
): AdvancedPagedResult<T> {
  return (
    value || {
      items: [],
      page: 0,
      pageSize: 0,
      pageCount: 0,
      totalItemCount: 0,
    }
  );
}

export interface RemoteScriptConfig {
  url: string;
  attributes?: ScriptAttributes;
  onload: () => void;
}

interface ScriptAttributes {
  [key: string]: string;
}

const scriptLogger = logger('script loader');

export const loadScript = (config: RemoteScriptConfig) => {
  const {url, attributes, onload} = config;
  scriptLogger.info('Loading Script', config);

  if (!url) {
    return;
  }

  const id = hash(url);
  let script: HTMLScriptElement = document.getElementById(id) as HTMLScriptElement;

  if (!script) {
    script = document.createElement('script');
    script.src = url;
    script.id = id;
    script.async = true;

    if (attributes) {
      map(attributes, (value, key) => {
        script.setAttribute(key, value);
      });
    }

    script.onload = () => {
      scriptLogger.info('Script Loaded', config);
      onload();
    };

    document.body.appendChild(script);
  } else {
    onload();
  }

  return script;
};

export const useScript = (config: RemoteScriptConfig) => {
  useEffect(() => {
    scriptLogger.info('useScript', config);
    loadScript(config);
  }, [config]);
};

export function parseUrl(url: string) {
  const parser = document.createElement('a');
  parser.href = url;

  const {href, protocol, hostname, port, pathname, search, hash, host, origin} = parser;
  return {href, protocol, hostname, port, pathname, search, hash, host, origin};
}
